// src/hooks/education-scope-hooks.ts
//
// Admin data hooks for the Education Scope screen (Admin ▸ Enablement ▸ Education Scope).
//
// These back the admin-curation of which Mindtickle series COUNT toward a learner's required-completion
// metric. Mindtickle's own INVITE_TYPE / MODULE_RELEVANCE columns are unreliable for this org, so an
// admin confirms per-series scope into `EducationScopeEntry` (the source of truth). Convenience
// `EducationScopeRule`s only DRAFT suggestions for the reconcile grid — they are never authoritative.
//
// Endpoints (toolbox_snowflake, admin-gated by `_education_root_authorized`; 400 in local/demo mode):
//   GET    snowflake/education/scope/draft/    ?root= ?only=drafted   → { candidates: [...] }
//   GET    snowflake/education/scope/entries/                          → { entries: [...] }
//   POST   snowflake/education/scope/entries/  (upsert one, keyed on series_id)
//   DELETE snowflake/education/scope/entries/  ?id= | ?series_id=
//   POST   snowflake/education/scope/confirm/  { entries: [...] }      (bulk upsert)
//   GET    snowflake/education/scope/rules/                            → { rules: [...] }
//   POST   snowflake/education/scope/rules/    (create, or update when { id })
//   DELETE snowflake/education/scope/rules/    ?id=

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { apiDelete, apiGet, apiPostJson } from '../support/aiApi';

/** The curated cadence a series is measured against. */
export type EducationWindow =
  | 'current_quarter'
  | 'current_fiscal_year'
  | 'always_on';

export const EDUCATION_WINDOW_OPTIONS: { value: EducationWindow; label: string }[] = [
  { value: 'current_quarter', label: 'Current quarter' },
  { value: 'current_fiscal_year', label: 'Current fiscal year' },
  { value: 'always_on', label: 'Always on' },
];

/** A confirmed per-series scope entry — the source of truth. */
export interface ScopeEntry {
  id: number;
  series_id: string;
  series_name: string;
  in_scope: boolean;
  required: boolean;
  window: EducationWindow;
  program_label: string;
  source: 'manual' | 'rule';
  notes: string;
  created_at: string | null;
  updated_at: string | null;
}

/** A convenience draft rule (first-match-wins over series name substrings). */
export interface ScopeRule {
  id: number;
  label: string;
  include: string[];
  exclude: string[];
  required: boolean;
  window: EducationWindow;
  program_label: string;
  active: boolean;
  order: number;
  created_at: string | null;
  updated_at: string | null;
}

/** The rule-derived suggestion for a series (no persisted state). */
export interface ScopeSuggestion {
  in_scope: boolean;
  required: boolean;
  window: EducationWindow;
  program_label: string;
  rule_label?: string;
}

/** One row of the reconcile grid: a distinct assigned series + its suggestion + current entry. */
export interface ScopeCandidate {
  series_id: string;
  series_name: string;
  suggested: ScopeSuggestion | null;
  entry: ScopeEntry | null;
  /** Mirrors the entry timestamps; null until the series has been curated. */
  created_at: string | null;
  updated_at: string | null;
}

/** How the draft grid is ordered. `updated` (default) = most-recently-curated first. */
export type ScopeSort = 'updated' | 'name';

export interface ScopeDraftResponse {
  success: boolean;
  root: string;
  sort: ScopeSort;
  series_count: number;
  candidate_count: number;
  candidates: ScopeCandidate[];
}

/** The payload a confirm/upsert accepts (series_id keyed). */
export interface ScopeEntryInput {
  series_id: string;
  series_name?: string;
  in_scope?: boolean;
  required?: boolean;
  window?: EducationWindow;
  program_label?: string;
  source?: 'manual' | 'rule';
  notes?: string;
}

export interface ScopeRuleInput {
  id?: number;
  label: string;
  include: string[];
  exclude: string[];
  required?: boolean;
  window?: EducationWindow;
  program_label?: string;
  active?: boolean;
  order?: number;
}

const SCOPE_KEY = 'education-scope';

/**
 * The reconcile grid: every distinct series assigned across the caller's org, each with its rule
 * suggestion and current entry. `retry: false` so the local-mode 400 surfaces immediately rather than
 * thrashing. Disabled until `enabled` (the tab is active) to avoid the heavy Snowflake call on mount.
 */
export function useScopeDraft(
  rootEmail?: string,
  onlyDrafted = false,
  enabled = true,
  sort: ScopeSort = 'updated',
) {
  return useQuery<ScopeDraftResponse>({
    queryKey: [SCOPE_KEY, 'draft', rootEmail ?? 'self', onlyDrafted, sort],
    queryFn: () =>
      apiGet<ScopeDraftResponse>('snowflake/education/scope/draft/', {
        root: rootEmail,
        only: onlyDrafted ? 'drafted' : undefined,
        // Default 'updated' matches the server default; sending it keeps the query key honest.
        sort,
      }),
    ...{ retry: false, staleTime: 5 * 60 * 1000, enabled },
  });
}

/** The confirmed source-of-truth entries. */
export function useScopeEntries(enabled = true) {
  return useQuery<{ success: boolean; entries: ScopeEntry[] }>({
    queryKey: [SCOPE_KEY, 'entries'],
    queryFn: () => apiGet('snowflake/education/scope/entries/'),
    ...{ retry: false, staleTime: 60 * 1000, enabled },
  });
}

/** The convenience draft rules. */
export function useScopeRules(enabled = true) {
  return useQuery<{ success: boolean; rules: ScopeRule[] }>({
    queryKey: [SCOPE_KEY, 'rules'],
    queryFn: () => apiGet('snowflake/education/scope/rules/'),
    ...{ retry: false, staleTime: 60 * 1000, enabled },
  });
}

/** Invalidate every scope query — call after any write so the grid/entries/rules refetch. */
function useInvalidateScope() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [SCOPE_KEY] });
}

/** Bulk-upsert entries from the reconcile grid. */
export function useConfirmScope() {
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (entries: ScopeEntryInput[]) =>
      apiPostJson('snowflake/education/scope/confirm/', { entries }),
    onSuccess: invalidate,
  });
}

/** Upsert a single entry (keyed on series_id). */
export function useUpsertEntry() {
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (entry: ScopeEntryInput) => apiPostJson('snowflake/education/scope/entries/', entry),
    onSuccess: invalidate,
  });
}

/** Delete an entry by id (or series_id). */
export function useDeleteEntry() {
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (params: { id?: number; series_id?: string }) =>
      apiDelete('snowflake/education/scope/entries/', params),
    onSuccess: invalidate,
  });
}

/** Create a rule, or update it when `id` is present. */
export function useUpsertRule() {
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (rule: ScopeRuleInput) => apiPostJson('snowflake/education/scope/rules/', rule),
    onSuccess: invalidate,
  });
}

/** Delete a rule by id. */
export function useDeleteRule() {
  const invalidate = useInvalidateScope();
  return useMutation({
    mutationFn: (id: number) => apiDelete('snowflake/education/scope/rules/', { id }),
    onSuccess: invalidate,
  });
}
